import { idToken, refreshSession } from './auth';
import type { DeleteRequest, Driver, Environment, ServerEvent, UpsertRequest } from './types';

export class ApiError extends Error { }

async function request<T>(env: Environment, method: string, path: string, body?: unknown, retry = true): Promise<T> {
    const response = await fetch(env.API_URL + path, {
        method,
        headers: { Authorization: `Bearer ${idToken()}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 401 && retry) {
        await refreshSession();
        return request(env, method, path, body, false);
    }
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new ApiError(result.message || `Request failed (${response.status})`);
    }
    return result;
}

export const fetchDrivers = (env: Environment) =>
    request<{ drivers: Driver[] }>(env, 'GET', '/drivers').then((result) => result.drivers);

export const upsertDriver = (env: Environment, body: UpsertRequest) =>
    request<{ driver: Driver }>(env, 'POST', '/drivers', body).then((result) => result.driver);

export const deleteDriver = (env: Environment, body: DeleteRequest) =>
    request<object>(env, 'POST', '/drivers/delete', body);

// API Gateway drops a websocket that has been silent for ten minutes.
const PING_INTERVAL = 4 * 60 * 1000;

// Keeps a websocket open for as long as the page lives, reconnecting with backoff.
// `onOpen` fires on every (re)connect: whatever happened while offline was missed, so reload then.
export function connectEvents(env: Environment, handlers: {
    onEvent: (event: ServerEvent) => void,
    onOpen: () => void,
    onClose: () => void,
}): () => void {
    let socket: WebSocket | undefined;
    let closed = false;
    let attempt = 0;
    let pingTimer: number | undefined;
    let retryTimer: number | undefined;

    const connect = () => {
        socket = new WebSocket(`${env.WS_URL}?token=${encodeURIComponent(idToken())}`);
        socket.onopen = () => {
            attempt = 0;
            socket!.send('hello');
            pingTimer = window.setInterval(() => socket!.send('ping'), PING_INTERVAL);
            handlers.onOpen();
        };
        socket.onmessage = (message) => handlers.onEvent(JSON.parse(message.data));
        socket.onclose = async () => {
            window.clearInterval(pingTimer);
            if (closed) {
                return;
            }
            handlers.onClose();
            attempt += 1;
            if (attempt === 2) {
                // Repeated refusals usually mean the token expired.
                await refreshSession();
            }
            retryTimer = window.setTimeout(connect, Math.min(15000, 1000 * 2 ** (attempt - 1)));
        };
    };
    connect();

    return () => {
        closed = true;
        window.clearTimeout(retryTimer);
        socket?.close();
    };
}
